import { useQuery } from "react-query";

import type {
  CampaignOrderData,
  GetEligibleCampaignsResponseType,
  GetProductPointsReturnType,
} from "@jti/rest-api-types";

import { LoyaltyApi } from "../../services";

export type LoyaltyUser = {
  uuid: string;
  ciamToken: string;
};

type ProductLoyaltyData = {
  productPoints: GetProductPointsReturnType | null;
  eligibleCampaigns: GetEligibleCampaignsResponseType[];
};

type UseProductLoyaltyQueryParams = {
  sku: string;
  quantity: number;
  unitPrice?: number | null;
  user?: LoyaltyUser;
  enabled: boolean;
};

const EMPTY_ELIGIBLE_CAMPAIGNS: GetEligibleCampaignsResponseType[] = [];

export const buildSingleProductCampaignOrder = (
  sku: string,
  unitPrice: number | null,
  quantity: number
): CampaignOrderData | undefined => {
  if (!unitPrice || unitPrice <= 0 || quantity <= 0) {
    return undefined;
  }

  return {
    orderTotal: unitPrice * quantity,
    items: {
      [sku]: {
        price: unitPrice,
        qty: quantity,
      },
    },
  };
};

export const useProductLoyaltyQuery = ({ sku, quantity, unitPrice, user, enabled }: UseProductLoyaltyQueryParams) => {
  return useQuery<ProductLoyaltyData>({
    queryKey: ["LOYALTY_PRODUCT_DATA", sku, quantity, unitPrice ?? null, user?.uuid ?? null, user?.ciamToken ?? null],
    queryFn: async () => {
      const loyaltyApi = LoyaltyApi.init();
      const order = buildSingleProductCampaignOrder(sku, unitPrice ?? null, quantity);

      const productPointsPromise = loyaltyApi.getProductPoints(sku, quantity, user?.uuid, user?.ciamToken);
      const eligibleCampaignsPromise =
        user && order
          ? loyaltyApi.getEligibleCampaigns(user.uuid, user.ciamToken, order)
          : Promise.resolve(EMPTY_ELIGIBLE_CAMPAIGNS);

      const [productPointsResult, eligibleCampaignsResult] = await Promise.allSettled([
        productPointsPromise,
        eligibleCampaignsPromise,
      ]);

      return {
        productPoints: productPointsResult.status === "fulfilled" ? productPointsResult.value : null,
        eligibleCampaigns: eligibleCampaignsResult.status === "fulfilled" ? eligibleCampaignsResult.value : [],
      };
    },
    enabled,
  });
};
